import React, { useEffect } from 'react';
import MediaQuery from 'react-responsive';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';
import cn from 'classnames';

import { DIALOG_TITLE } from '../../../constants/strings';
import { ADDRESS, PATIENT_REGISTRATION } from '../../../constants/routes';
import { ADD_PATIENT_STEPS } from '../../../constants/props';
import { usePageRedirect } from '../utils/usePageRedirect';
import { getSystemError } from '../utils/convertErrors';
import { BREAKPOINT } from '../../../constants/styles';
import { checkErrors } from '../utils/checkErrors';
import BackButton from '../../common/BackButton';
import ErrorBlock from '../../common/ErrorBlock';
import PatientInfoForm from './PatientInfoForm';
import Loader from '../../common/Loader';
import Button from '../../Button';
import {
  usePatientFormState,
  usePatientValidation,
  validateRequiredFields,
  PATIENT_INFO_FIELDS,
} from './usePatientForm';
import { enhance } from './enhance';

const PatientRegistrationInfo = ({
  validateData,
  personalInfo,
  formatMessage,
  savePatientData,
  nameSuffixList,
  patientContacts,
  validationError,
  validationPending,
  clearValidationData,
  clearPatientDataByKey,
}) => {
  const {
    patientState,
    handlePatientFormChange,
    clearPatientForm,
  } = usePatientFormState({ ...personalInfo });

  const { validateErrors } = usePatientValidation(validationError);

  usePageRedirect(PATIENT_REGISTRATION, [patientContacts]);

  const systemError = getSystemError(validationError);
  const isValid = validateRequiredFields(patientState);

  useEffect(() => {
    if (validationError && !checkErrors(validationError, PATIENT_INFO_FIELDS)) {
      savePatientData({ [ADD_PATIENT_STEPS.PERSONAL_INFO]: patientState });
      browserHistory.push(`${PATIENT_REGISTRATION}/${ADDRESS}`);
    }
    return () => clearValidationData();
  }, [validationError]);

  const handleBack = () => {
    browserHistory.goBack();
  };

  const handleSubmit = e => {
    e.preventDefault();
    validateData(patientState);
  };

  const handleClear = () => {
    clearPatientForm();
    clearValidationData();
    clearPatientDataByKey({ key: ADD_PATIENT_STEPS.PERSONAL_INFO });
  };

  return (
    <div className="Root">
      <div className="g-content" styleName="root">
        <div styleName="header">
          <MediaQuery minWidth={BREAKPOINT}>
            <BackButton onBack={handleBack} />
          </MediaQuery>
        </div>
        <If condition={validationPending}>
          <Loader className="g-centeredAll" />
        </If>
        <div styleName={cn('content', { disabled: validationPending })}>
          <form onSubmit={handleSubmit} styleName="form">
            <label styleName="form--label" className="mb4">
              {formatMessage(DIALOG_TITLE.addPatientTitle)}
            </label>
            <span styleName="form--subtitle">
              {formatMessage({ id: 'profile.section.personal' })}
            </span>
            {systemError && <ErrorBlock errors={systemError} className="mt3" />}

            <PatientInfoForm
              {...patientState}
              nameSuffixList={nameSuffixList}
              formatMessage={formatMessage}
              validateErrors={validateErrors}
              onChange={handlePatientFormChange}
            />
            <div styleName="form--btn" className="mt4">
              <Button
                className="mr3"
                type="submit"
                raised
                color="primary"
                disabled={!isValid}
              >
                {formatMessage({ id: 'common.next' })}
              </Button>
              <Button raised color="accent" onClick={handleClear} type="reset">
                {formatMessage({ id: 'common.clear' })}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

PatientRegistrationInfo.propTypes = {
  validateData: PropTypes.func,
  clearValidationData: PropTypes.func,
  nameSuffixList: PropTypes.array,
  formatMessage: PropTypes.func,
  validationPending: PropTypes.bool,
  validationError: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  patientContacts: PropTypes.object,
  savePatientData: PropTypes.func,
  personalInfo: PropTypes.object,
  clearPatientDataByKey: PropTypes.func,
};

export default enhance(PatientRegistrationInfo);



// WEBPACK FOOTER //
// ./src/components/PatientRegistration/PatientRegistrationInfo/index.jsx